import html2pdf from "html2pdf.js";
import { format } from "date-fns";
import * as Sentry from "@sentry/react";

/**
 * Replaces placeholders in contract text with actual values
 * @param text The contract text with placeholders
 * @param replacements Object containing key-value pairs for replacements
 * @returns The text with placeholders replaced
 */
export const replaceContractPlaceholders = (
  text: string,
  replacements: Record<string, string>
): string => {
  let processedText = text;

  // Replace each placeholder with its corresponding value
  Object.entries(replacements).forEach(([key, value]) => {
    const placeholder = new RegExp(`{{${key}}}`, "g");
    processedText = processedText.replace(placeholder, value);
  });

  return processedText;
};

/**
 * Generates HTML for the signature section
 * @param signatureUrl URL of the signature image
 * @param applicantName Name of the applicant
 * @param date Formatted date string
 * @returns HTML string for the signature section
 */
export const generateSignatureHtml = (
  signatureUrl: string,
  applicantName: string,
  date: string
): string => {
  return `
    <div class="signature-section" style="margin-top: 40px; padding-top: 20px;">
      <h3 style="font-size: 14px; font-weight: bold; margin-bottom: 15px;">Signature:</h3>
      <div style="text-align: left;">
        <img src="${signatureUrl}" alt="Signature" style="max-width: 350px;" />
      </div>
      <div style="border-top: 2px solid #000; margin-top: 5px; padding-top: 5px;">
        <p style="margin: 5px 0; font-size: 12px;">Name: ${applicantName}</p>
        <p style="margin: 5px 0; font-size: 12px;">Date: ${date}</p>
      </div>
    </div>
  `;
};

/**
 * Generates a PDF document that combines contract text and signature
 * @param contractText The text content of the contract
 * @param signatureUrl URL of the signature image
 * @param applicantName The name of the applicant to include in the document
 * @returns A Blob containing the generated PDF
 */
export const generateContractPdf = async (
  contractText: string,
  signatureUrl: string,
  applicantName: string,
  driverNumber?: string,
  companyName?: string,
  address?: string,
  vatNumber?: string,
  salary?: string
): Promise<Blob> => {
  // Format current date for the contract
  const currentDate = format(new Date(), "MMMM d, yyyy");

  // Replace placeholders in the contract text
  const processedText = replaceContractPlaceholders(contractText, {
    "full-name": applicantName,
    "driver-number": driverNumber || "",
    "company-name-or-full-name": companyName || "",
    address: address || "",
    "vat-number": vatNumber || "",
    salary: salary || "",
    date: currentDate,
  });

  // Generate signature HTML
  const signatureHtml = generateSignatureHtml(
    signatureUrl,
    applicantName,
    currentDate
  );

  const options = {
    filename: "contract.pdf",
    margin: 25,
    image: { type: "jpeg", quality: 0.98 },
    html2canvas: { scale: 2, removeContainer: true, useCORS: true },
    jsPDF: { unit: "pt", format: "a4", orientation: "portrait" },
    pagebreak: { mode: "avoid-all" },
  };

  try {
    // Convert the HTML to a PDF Blob
    const pdfBlob = await html2pdf()
      .set(options)
      .from(`<div class="reset-all">${processedText}${signatureHtml}</div>`)
      .outputPdf("blob");
    return pdfBlob;
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        context: "generateContractPdf",
        contractText,
        signatureUrl,
        applicantName,
        driverNumber,
        companyName,
        address,
        vatNumber,
        salary,
        currentDate,
      },
    });
    console.error("Error generating PDF:", error);
    throw error;
  }
};

/**
 * Converts a Blob to a File object
 * @param blob The Blob to convert
 * @param filename The name for the file
 * @param type The MIME type of the file
 * @returns A File object
 */
export const blobToFile = (
  blob: Blob,
  filename: string,
  type: string = "application/pdf"
): File => {
  return new File([blob], filename, { type });
};
